import { createFileRoute } from '@tanstack/react-router';
import { CreditCard, Users, TrendingUp, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { KPI } from '@/components/ui/kpi';
import {
  useMasterSubscriptions,
  useMasterBillingStats,
  formatBrl,
  statusLabel,
  statusTone,
  paymentMethodLabel,
  type SubscriptionStatus,
} from '@/lib/hooks/billing';

export const Route = createFileRoute('/_app/master/billing')({
  component: MasterBillingPage,
});

function MasterBillingPage() {
  const subs = useMasterSubscriptions();
  const stats = useMasterBillingStats();

  const items = subs.data?.items ?? [];
  const s = stats.data;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <Eyebrow>Billing</Eyebrow>
        <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
          Assinaturas e cobranças
        </h1>
        <p className="text-sm text-fg-muted mt-2">
          Visão consolidada das assinaturas Asaas de todos os workspaces.
        </p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI
          label="MRR"
          value={s ? formatBrl(s.mrr) : '—'}
          icon={TrendingUp}
          tone="mint"
        />
        <KPI
          label="Ativas"
          value={s ? String(s.active) : '—'}
          icon={CreditCard}
          tone="ink"
        />
        <KPI
          label="Pendentes"
          value={s ? String(s.pending) : '—'}
          icon={Users}
          tone="clay"
        />
        <KPI
          label="Canceladas"
          value={s ? String(s.cancelled) : '—'}
          icon={AlertTriangle}
          tone="lilac"
        />
      </section>

      <Card variant="default" className="p-0 overflow-hidden">
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-card-stroke">
          <div>
            <Eyebrow>Assinaturas</Eyebrow>
            <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
              {items.length} {items.length === 1 ? 'workspace' : 'workspaces'}
            </h3>
          </div>
        </header>

        {subs.isLoading ? (
          <div className="p-6 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 rounded-2 bg-card-soft animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-fg-muted">Nenhuma assinatura criada ainda.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card-soft text-[10px] uppercase tracking-wider text-fg-muted font-mono">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">Workspace</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Plano</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Valor</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Método</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Próximo</th>
                  <th className="text-left px-5 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-card-stroke">
                {items.map((row) => (
                  <tr key={row.id} className="hover:bg-card-soft">
                    <td className="px-5 py-3">
                      <p className="font-medium text-fg">{row.workspaceName ?? '—'}</p>
                      <p className="text-xs text-fg-muted font-mono">{row.workspaceId}</p>
                    </td>
                    <td className="px-3 py-3 text-fg">
                      {planName(row.plan)}
                      <span className="block text-xs text-fg-muted">
                        {row.cycle === 'YEARLY' ? 'anual' : 'mensal'}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-medium text-fg">
                      {formatBrl(row.value)}
                    </td>
                    <td className="px-3 py-3 text-fg-muted">
                      {paymentMethodLabel(row.paymentMethod)}
                    </td>
                    <td className="px-3 py-3 text-fg-muted">
                      {row.nextDueDate
                        ? new Date(row.nextDueDate).toLocaleDateString('pt-BR')
                        : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <Pill tone={statusTone(row.status as SubscriptionStatus)} dot>
                        {statusLabel(row.status as SubscriptionStatus)}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function planName(plan: string): string {
  return { BASIC: 'Básico', PRO: 'Profissional', ENTERPRISE: 'Empresarial' }[plan] ?? plan;
}
